import React, { useState, useEffect } from "react";
import { Box, Button, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from "xlsx";
import { tokens } from "../../theme";
import Header from "../../components/Header";

const Invoices = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);

  // Styles
  const buttonStyles = {
    backgroundColor: colors.greenAccent[500],
    color: "white",
    fontSize: "18px",
    fontFamily: "Calibri",
    fontWeight: "600",
    textTransform: 'none',

    "&:hover": {
      backgroundColor: colors.greenAccent[700],
    },
  };

  const spinnerStyles = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    border: "16px solid #f3f3f3",
    borderTop: "16px solid #3498db",
    borderRadius: "50%",
    width: "60px",
    height: "60px",
    animation: "spin 2s linear infinite",
  };

  const errorPopupStyles = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    color: "white",
    padding: "20px",
    borderRadius: "10px",
    textAlign: "center",
    zIndex: 1000,
  };
  const csvHeaders = [
    "S.no,IG Handle,IG link,Contact,Foll(K),Avg views,Segment,Category,Gender,Location,Language,Eng rate"
  ];
  // Function to download the CSV file
  const downloadCSV = () => {
    const csvContent = csvHeaders.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "Influencer_Marketing_Headers_Only.csv");
    link.click();
  };
  // Function to handle export when clicked
  const handleExportCSV = async () => {
    const myAPIURI = "https://api.dmaverix.com";
    const myAuthToken = localStorage.getItem("token");

    if (!myAuthToken) {
      alert("Authentication token not found. Please log in.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${myAPIURI}/api/v2/influencers/export`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${myAuthToken}`,
        },
      });

      setIsLoading(false);

      if (!response.ok) {
        throw new Error("Failed to fetch the file");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "influencers_data.csv";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setIsLoading(false);
      setError("An error occurred while downloading the file.");
      console.error("Error exporting CSV:", error);
    }
  };

  const closeErrorPopup = () => {
    setError(null); // Hide the error popup
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const selectedFile = event.target.files[0];

    if (!selectedFile) return;

    // Check file type before processing
    const fileType = selectedFile.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx"].includes(fileType)) {
      alert("Please upload a valid .csv or .xlsx file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      await processExcelData(jsonData, selectedFile);
    };

    reader.readAsArrayBuffer(selectedFile);
  };

  // Process the Excel data
  const processExcelData = async (data, selectedFile) => {
    if (data.length > 1) {
      const headers = data[0];
      const formattedColumns = headers.map((header) => ({
        field: header,
        headerName: header,
        flex: 1,
      }));

      const formattedRows = data.slice(1).map((row, index) => {
        const rowData = {};
        headers.forEach((header, idx) => {
          rowData[header] = row[idx] || "";
        });
        rowData.id = index + 1;
        return rowData;
      });

      setColumns(formattedColumns);
      setRows(formattedRows);

      // Store data in localStorage
      localStorage.setItem('influencerData', JSON.stringify({ columns: formattedColumns, rows: formattedRows }));

      await handleDataUpload(selectedFile);
    }
  };

  // Upload the file to the backend
  const handleDataUpload = async (file) => {
    setIsLoading(true);

    const myAPIURI = "https://api.dmaverix.com";
    const myAuthToken = localStorage.getItem("token");

    if (!myAuthToken) {
      alert("Authentication token not found. Please log in.");
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${myAPIURI}/api/v2/influencers/import`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${myAuthToken}`,
        },
        body: formData,
      });

      setIsLoading(false);

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error response from API:", errorData);
        throw new Error("Failed to upload data");
      }

      const data = await response.json();
      console.log("File data successfully processed and stored in the database.");

      // Log the response from the backend to check if the data was added
      console.log("Backend response:", data);

      if (data.success) {
        alert("Data uploaded successfully.");
      } else {
        alert("Failed to upload data.");
      }
    } catch (error) {
      setIsLoading(false);
      setError("An error occurred while uploading the data.");
      console.error("Error uploading data:", error);
    }
  };

  // Fetch data from API or use localStorage
  const fetchData = async () => {
    const storedData = localStorage.getItem('influencerData');

    if (storedData) {
      const parsedData = JSON.parse(storedData);
      setColumns(parsedData.columns);
      setRows(parsedData.rows);
    } else {
      const myAPIURI = "https://api.dmaverix.com";
      const myAuthToken = localStorage.getItem("token");

      if (!myAuthToken) {
        alert("Authentication token not found. Please log in.");
        return;
      }

      try {
        const response = await fetch(`${myAPIURI}/api/v2/influencers`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${myAuthToken}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }

        const data = await response.json();

        if (Array.isArray(data)) {
          const rows = data.map((item) => ({
            id: item.id,
            IGHandle: item.IGHandle,
            IGLink: item.IGLink,
            Contact: item.Contact,
            Followers: item.Followers,
            AvgViews: item.AvgViews,
            Segment: item.Segment,
            Category: item.Category,
            Location: item.Location,
            Gender: item.Gender,
            EngRate: item.EngRate,
          }));

          setRows(rows);

          setColumns([
            { field: "id", headerName: "ID", flex: 0.5 },
            { field: "IGHandle", headerName: "Instagram Handle", flex: 1 },
            { field: "IGLink", headerName: "Instagram Link", flex: 1 },
            { field: "Contact", headerName: "Contact", flex: 1 },
            { field: "Followers", headerName: "Followers", type: "number", flex: 1 },
            { field: "AvgViews", headerName: "Avg Views", type: "number", flex: 1 },
            { field: "Segment", headerName: "Segment", flex: 1 },
            { field: "Category", headerName: "Category", flex: 1 },
            { field: "Location", headerName: "Location", flex: 1 },
            { field: "Gender", headerName: "Gender", flex: 1 },
            { field: "EngRate", headerName: "EngRate", flex: 1 },
          ]);
        } else {
          console.warn("Received non-array data:", data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  // Handle logout (clear localStorage)
  const handleLogout = () => {
    localStorage.removeItem('influencerData'); // Clear the influencer data from localStorage
    // Optionally, you can redirect the user to a login page
    window.location.href = "/login";
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Box m="20px">
      <Header title="INFLUENCER INVENTORY" subtitle="Upload CSV File Only" />
      <Box display="flex" justifyContent="space-between" alignItems="center" my={2}>
        <div className="upload-button">
          <Button variant="contained" component="label" sx={buttonStyles}>
            + Upload File
            <input
              type="file"
              accept=".csv, .xlsx"
              hidden
              onChange={handleFileUpload}
            />
            <FileUploadIcon style={{ marginLeft: '5px' }} />
          </Button>
        </div>
        <div className="both-buttons" style={{display:'flex', gap:'20px'}}>
          <Button
            variant="contained"
            color="primary"
            onClick={downloadCSV}
            sx={buttonStyles}
          >
            Sample csv
            <FileDownloadIcon style={{ marginLeft: '5px' }} />
          </Button>
          <Button
            variant="contained"
            sx={buttonStyles}
            onClick={handleExportCSV}
            disabled={isLoading}
          >
            {isLoading ? "Exporting..." : "Export File"}
            <FileDownloadIcon style={{ marginLeft: '5px' }} />
          </Button>
        </div>
      </Box>

      {isLoading && <Box sx={spinnerStyles}></Box>}

      {error && (
        <Box sx={errorPopupStyles}>
          <p>{error}</p>
          <Button
            variant="contained"
            sx={{ backgroundColor: "red", color: "white" }}
            onClick={closeErrorPopup}
          >
            Close
          </Button>
        </Box>
      )}

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid lightgray",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "1px solid lightgray",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          checkboxSelection
        />
      </Box>
    </Box>
  );
};

export default Invoices;

